import { useCurrentStateAndParams } from "@uirouter/react";
import type { AnyObject } from "antd/es/_util/type";
import capitalize from "lodash/capitalize";
import { useSelector } from "react-redux";

import { SWReactIcons } from "@similarweb/icons";
import type { IconName } from "@similarweb/icons";

import { benchmarkTrackerTracking } from "app/pages/benchmark-tracker/services/tracking";
import { selectSelectedBenchmark } from "app/pages/benchmark-tracker/store/selectors/entitySelectors";
import type { WebsiteLinkMetricName } from "app/pages/benchmark-tracker/types/common";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { TextWithTooltipCell } from "app/components/React/Table/cells/TextWithTooltipCell";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { METRIC_PARAMS } from "./config";
import * as SC from "./style";

type WebsiteWithFaviconCellSchema = {
    tooltipClassName?: string;
    enableExternalLink?: boolean;
    showTooltip?: boolean;
    primaryIcon?: IconName;
    metricName?: WebsiteLinkMetricName;
    value: string;
    row?: AnyObject;
};

const WebsiteWithFaviconCell = ({
    value,
    row,
    tooltipClassName,
    enableExternalLink,
    showTooltip,
    primaryIcon,
    metricName,
}: WebsiteWithFaviconCellSchema) => {
    const { defaultIcon, isPrimary } = row ?? {};
    const { params } = useCurrentStateAndParams();
    const selectedBenchmark = useSelector(selectSelectedBenchmark);
    const track = benchmarkTrackerTracking.metric;

    const onClick = async () => {
        if (metricName) {
            const primaryDomain = selectedBenchmark?.primaryDomains[0];

            const { country, granularity, duration, webSource } = params;
            const keyList = [primaryDomain];
            const newParams = { isWWW: "*", country, duration, granularity, webSource };

            const { state, additionalParams } = METRIC_PARAMS[metricName];

            if (!isPrimary) {
                keyList.push(value);
            }

            const key = keyList.join(",");
            track.trackLinkClick(isPrimary ? "primary" : "competitor", metricName);

            await proNavigator.go(state, { ...newParams, ...additionalParams, key });
        }
    };

    if (defaultIcon === null) {
        return (
            <SC.StyledTextCell onClick={onClick}>
                <TextWithTooltipCell value={capitalize(translate(value))} />
            </SC.StyledTextCell>
        );
    }

    const primaryDomain = (
        <SC.PrimaryStringContainer>
            {value}
            <SC.PrimaryDomainText>({capitalize(translate("common.you"))})</SC.PrimaryDomainText>
            {primaryIcon ? (
                <SWReactIcons
                    iconName={primaryIcon}
                    size="xs"
                />
            ) : null}
        </SC.PrimaryStringContainer>
    );

    return (
        <SC.StyledDomain
            isLink={!!metricName}
            onClick={onClick}
            isPrimary={isPrimary}
        >
            <CoreWebsiteCell
                icon={defaultIcon}
                //TODO: rework props in CoreWebsiteCell, add possibility to pass domain as Element, not only string
                // @ts-ignore
                domain={isPrimary ? primaryDomain : value}
                externalLink={enableExternalLink ? `http://${value}` : undefined}
                tooltipClassName={tooltipClassName}
                showToolTip={showTooltip}
            />
        </SC.StyledDomain>
    );
};

export default WebsiteWithFaviconCell;
